import { News } from '@arpeggi/shared'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { styled } from '../../../lib/stitches'
import { useCreateNews, useUpdateNews } from '../../../queries/news'
import Button from '../../core/Button/Button'
import { FieldErrorLabel } from '../../core/FieldErrorLabel'
import { FieldLabel } from '../../core/FieldLabel'
import { Flex } from '../../core/Flex'
import Text from '../../core/Text/Text'
import { TextInput } from '../../core/TextInput'
import { InternalEndpoint } from '../../Internal'
import { FileUpload } from '../../internal/components/common'
import { Spinner } from '../../platform/explore/common/utils'
import { useSnackBar } from '../hooks/useSnackBar'

interface Props {
  news?: News
}

interface FormFields {
  title: string
  subtitle: string
  link: string
  image: File
}

const StyledForm = styled('form', {
  display: 'flex',
  gap: '8px',
  flexDirection: 'column',
  maxWidth: '1280px'
})

export const NewsForm: React.FC<Props> = ({ news }) => {
  const { dispatchSnack } = useSnackBar()
  const [file, setFile] = useState<File>(undefined)
  const [fileError, setFileError] = useState<string>(undefined)
  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<FormFields>({
    mode: 'onBlur',
    defaultValues: {
      title: news?.metadata.title,
      subtitle: news?.metadata.subtitle,
      link: news?.metadata.link
    }
  })
  const updateNews = useUpdateNews()
  const createNews = useCreateNews()
  const navigate = useNavigate()
  const isUpdateMode = news !== undefined

  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    if (!file && !isUpdateMode) {
      setFileError('Image is required')
      return
    }

    const dto = {
      image: file,
      metadata: { title: data.title, subtitle: data.subtitle, link: data.link }
    }

    if (isUpdateMode) {
      updateNews.mutateAsync(
        {
          id: news.id,
          dto
        },
        {
          onSuccess: () => {
            dispatchSnack('News item updated!')
          }
        }
      )
    } else {
      createNews.mutateAsync(
        { dto },
        {
          onSuccess: () => {
            navigate(`/${InternalEndpoint}/news`)
            dispatchSnack('News item created!')
          }
        }
      )
    }
  }

  const imagePreviewUrl = file ? URL.createObjectURL(file) : news?.image.url

  return (
    <>
      <Text size="24" css={{ marginBottom: '$16' }}>
        {isUpdateMode ? 'Update News' : 'Create News'}
      </Text>
      <StyledForm onSubmit={handleSubmit(onSubmit)}>
        <Flex direction="column" width="full">
          <FieldLabel htmlFor="title">Title</FieldLabel>
          <Controller
            name="title"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Required'
              },
              maxLength: {
                value: 40,
                message: 'At most 40 characters'
              }
            }}
            render={({ field }) => <TextInput id="title" placeholder="Title" {...field} />}
          />
          <FieldErrorLabel>{errors.title?.message}</FieldErrorLabel>
        </Flex>
        <Flex direction="column" width="full">
          <FieldLabel htmlFor="title">Subtitle</FieldLabel>
          <Controller
            name="subtitle"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Required'
              },
              maxLength: {
                value: 40,
                message: 'At most 40 characters'
              }
            }}
            render={({ field }) => <TextInput id="subtitle" placeholder="Subtitle" {...field} />}
          />
          <FieldErrorLabel>{errors.subtitle?.message}</FieldErrorLabel>
        </Flex>
        <Flex direction="column" width="full">
          <FieldLabel htmlFor="title">Link</FieldLabel>
          <Controller
            name="link"
            control={control}
            rules={{
              required: {
                value: true,
                message: 'Required'
              }
            }}
            render={({ field }) => <TextInput id="link" placeholder="Link" {...field} />}
          />
          <FieldErrorLabel>{errors.link?.message}</FieldErrorLabel>
        </Flex>
        <Flex direction="column" width="full">
          <FileUpload
            fieldName="image"
            label="Image"
            onChange={(e) => setFile(e.target.files[0])}
            errorMessage={fileError}
            imagePreviewUrl={imagePreviewUrl}
          />
        </Flex>
        <Button
          type={'dark'}
          big={true}
          rounded={true}
          disabled={updateNews.isLoading}
          onClick={handleSubmit(onSubmit)}
          css={{ maxWidth: '300px' }}
        >
          {updateNews.isLoading ? (
            <Spinner />
          ) : (
            <Text type={'primary_inverted'} size={'14'}>
              {isUpdateMode ? 'Save changes' : 'Save'}
            </Text>
          )}
        </Button>
      </StyledForm>
    </>
  )
}
